/*
 * <blog-overview> custom element.
 *
 * Owns client-side filter / sort / pagination for the Blog Overview section.
 * The section renders the first page server-side and embeds the full article
 * list as JSON ([data-blog-overview-data]); this element reads that payload and
 * (re)renders the "abstract" pattern grid + numbered pagination on demand.
 *
 * The rendered card markup mirrors snippets/novicell_blog-overview-card.liquid —
 * keep the two in sync.
 */

const URL_PREFIX = 'blog';
// Desktop pattern cycle (matches the Liquid SSR + CSS): index within the page
// mod 8 → slot role. 3 = full-width feature (image left), 7 = feature (image right).
const SLOT_BY_POS = ['short', 'short', 'tall', 'feature-left', 'tall', 'short', 'short', 'feature-right'];
// Responsive image hints per slot, mirroring the card snippet.
const IMG_HINTS = {
  short: { widths: [300, 400, 500, 700, 900], sizes: '(min-width: 990px) 25vw, 100vw' },
  tall: { widths: [400, 600, 800, 1000, 1300], sizes: '(min-width: 990px) 40vw, 100vw' },
  feature: { widths: [600, 900, 1200, 1500, 1800], sizes: '(min-width: 990px) 56vw, 100vw' },
};
const EAGER_CARDS = 3;

class BlogOverview extends HTMLElement {
  constructor() {
    super();
    this._onPopState = this._onPopState.bind(this);
    this._onShopifySection = this._onShopifySection.bind(this);
  }

  connectedCallback() {
    this.grid = this.querySelector('[data-blog-grid]');
    if (!this.grid) return;

    this.dataScript = this.querySelector('[data-blog-overview-data]');
    this.sortSelect = this.querySelector('[data-blog-sort]');
    this.pagination = this.querySelector('[data-blog-pagination]');
    this.paginationFallback = this.querySelector('[data-blog-pagination-fallback]');
    this.filtersEl = this.querySelector('[data-blog-filters]');
    this.filtersToggle = this.querySelector('[data-blog-filters-toggle]');

    // Pagination button labels come from the section's locale strings.
    const paginationData = this.pagination ? this.pagination.dataset : {};
    this.labels = {
      previous: paginationData.labelPrevious || 'Previous page',
      next: paginationData.labelNext || 'Next page',
      page: paginationData.labelPage || 'Page __n__',
    };

    this.pageSize = parseInt(this.dataset.pageSize, 10) || 8;
    this.defaultSort = this.dataset.defaultSort || 'newest';
    // On a /tagged/ URL the embedded JSON only holds that tag's articles, so we
    // must not pretend to own the complete dataset — let filter links navigate.
    this.hasCompleteData = !this.dataset.currentTag;

    this.articles = this._parseData();
    if (!this.articles.length) return;

    this.state = {
      tag: this.dataset.currentTag || 'all',
      sort: this.defaultSort,
      page: 1,
    };
    this._parseURL();

    // JS owns pagination now; drop the no-JS fallback and expose the JS nav.
    if (this.paginationFallback) this.paginationFallback.remove();
    if (this.pagination) this.pagination.hidden = false;

    this._bindListeners();
    this._syncControls();

    // Leave the server-rendered grid in place when nothing deviates from the
    // default view (avoids a re-render flash on first paint); otherwise rebuild.
    if (this.state.tag !== 'all' || this.state.sort !== this.defaultSort || this.state.page !== 1) {
      this._renderGrid();
    }
    this._renderPagination();

    window.addEventListener('popstate', this._onPopState);
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', this._onShopifySection);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this._onPopState);
    document.removeEventListener('shopify:section:select', this._onShopifySection);
  }

  /* ---------- Data ---------- */

  _parseData() {
    if (!this.dataScript) return [];
    try {
      const parsed = JSON.parse(this.dataScript.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /* ---------- URL state ---------- */

  _parseURL() {
    const params = new URLSearchParams(window.location.search);
    const tag = params.get(`${URL_PREFIX}.tag`);
    if (tag) this.state.tag = tag;
    const sort = params.get(`${URL_PREFIX}.sort`);
    if (sort) this.state.sort = sort;
    const page = parseInt(params.get(`${URL_PREFIX}.page`), 10);
    if (page > 0) this.state.page = page;
  }

  _syncURL({ replace = false } = {}) {
    // Don't rewrite the URL on tagged pages — the path already carries the tag.
    if (!this.hasCompleteData) return;
    const url = new URL(window.location.href);
    Array.from(url.searchParams.keys())
      .filter((k) => k.startsWith(`${URL_PREFIX}.`))
      .forEach((k) => url.searchParams.delete(k));

    if (this.state.tag !== 'all') url.searchParams.set(`${URL_PREFIX}.tag`, this.state.tag);
    if (this.state.sort !== this.defaultSort) url.searchParams.set(`${URL_PREFIX}.sort`, this.state.sort);
    if (this.state.page !== 1) url.searchParams.set(`${URL_PREFIX}.page`, this.state.page);

    window.history[replace ? 'replaceState' : 'pushState']({}, '', url.toString());
  }

  _onPopState() {
    this.state = { tag: this.dataset.currentTag || 'all', sort: this.defaultSort, page: 1 };
    this._parseURL();
    this._syncControls();
    this._renderGrid();
    this._renderPagination();
  }

  _onShopifySection(event) {
    if (!event.detail || event.detail.sectionId !== this.dataset.sectionId) return;
    this._renderGrid();
    this._renderPagination();
  }

  /* ---------- Listeners ---------- */

  _bindListeners() {
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', () => {
        this.state.sort = this.sortSelect.value;
        this.state.page = 1;
        this._renderGrid();
        this._renderPagination();
        this._syncURL();
      });
    }

    // Filter links: intercept only when we hold the complete dataset. On tagged
    // URLs the links navigate normally so the server returns full/correct data.
    if (this.filtersEl && this.hasCompleteData) {
      this.filtersEl.addEventListener('click', (e) => {
        const link = e.target.closest('[data-blog-filter]');
        if (!link) return;
        e.preventDefault();
        this.state.tag = link.dataset.blogFilter;
        this.state.page = 1;
        this._syncControls();
        this._renderGrid();
        this._renderPagination();
        this._syncURL();
        this._closeFilters();
      });
    }

    // Mobile filters disclosure.
    if (this.filtersToggle) {
      this.filtersToggle.addEventListener('click', () => {
        const open = this.filtersEl.classList.toggle('is-open');
        this.filtersToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    // Numbered pagination (delegated).
    if (this.pagination) {
      this.pagination.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-blog-page]');
        if (!btn) return;
        e.preventDefault();
        const page = parseInt(btn.dataset.blogPage, 10);
        if (!page || page === this.state.page) return;
        this.state.page = page;
        this._renderGrid();
        this._renderPagination();
        this._syncURL();
        this._scrollToTop();
      });
    }
  }

  _closeFilters() {
    if (this.filtersEl) this.filtersEl.classList.remove('is-open');
    if (this.filtersToggle) this.filtersToggle.setAttribute('aria-expanded', 'false');
  }

  _scrollToTop() {
    const top = this.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  /* ---------- Derived list ---------- */

  _filteredSorted() {
    let list = this.articles.slice();
    if (this.state.tag && this.state.tag !== 'all') {
      list = list.filter((a) => tagHandles(a).includes(this.state.tag));
    }
    if (this.state.sort === 'oldest') {
      list.sort((a, b) => (a.date_iso || '').localeCompare(b.date_iso || ''));
    } else if (this.state.sort === 'az') {
      list.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    } else {
      list.sort((a, b) => (b.date_iso || '').localeCompare(a.date_iso || ''));
    }
    return list;
  }

  _pageCount(total) {
    return Math.max(1, Math.ceil(total / this.pageSize));
  }

  /* ---------- Rendering ---------- */

  _syncControls() {
    if (this.sortSelect) this.sortSelect.value = this.state.sort;
    this.querySelectorAll('[data-blog-filter]').forEach((link) => {
      const active = link.dataset.blogFilter === this.state.tag;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
  }

  _renderGrid() {
    const list = this._filteredSorted();
    const pageCount = this._pageCount(list.length);
    if (this.state.page > pageCount) this.state.page = pageCount;
    const start = (this.state.page - 1) * this.pageSize;
    const pageItems = list.slice(start, start + this.pageSize);

    this.grid.textContent = '';

    if (!pageItems.length) {
      const empty = document.createElement('p');
      empty.className = 'blog-overview__empty tw-font-secondary';
      empty.textContent = this.dataset.emptyText || '';
      this.grid.appendChild(empty);
      return;
    }

    const frag = document.createDocumentFragment();
    let triptych = null;

    pageItems.forEach((article, i) => {
      const pos = i % 8;
      const slot = SLOT_BY_POS[pos];
      const isFeature = slot.indexOf('feature') === 0;

      if (isFeature && triptych) {
        frag.appendChild(triptych);
        triptych = null;
      }

      if (pos === 0 || pos === 4) {
        if (triptych) frag.appendChild(triptych);
        triptych = document.createElement('div');
        triptych.className =
          'blog-overview__row blog-overview__row--triptych blog-overview__row--tall-' +
          (pos === 0 ? 'right' : 'left');
      }

      const card = this._buildCard(article, i, slot);
      if (isFeature) {
        frag.appendChild(card);
      } else {
        (triptych || frag).appendChild(card);
      }

      if (pos === 2 || pos === 6) {
        if (triptych) {
          frag.appendChild(triptych);
          triptych = null;
        }
      }
    });
    if (triptych) frag.appendChild(triptych);

    this.grid.appendChild(frag);
  }

  _buildCard(article, index, slot) {
    const isFeature = slot.indexOf('feature') === 0;
    const block = isFeature ? 'blog-overview__feature' : 'blog-overview__card';

    const article_el = document.createElement('article');
    article_el.className = `${block} ${block}--${slot}`;
    article_el.setAttribute('data-blog-card', '');
    article_el.dataset.index = String(index);
    article_el.dataset.slot = slot;

    const link = document.createElement('a');
    link.className = `${block}-link`;
    link.href = article.url;

    const media = document.createElement('div');
    media.className = `${block}-media`;
    media.appendChild(this._buildMedia(article, index, slot));
    link.appendChild(media);

    const info = document.createElement('div');
    info.className = `${block}-info`;

    if (article.date_display) {
      const time = document.createElement('time');
      time.className = 'blog-overview__date tw-font-secondary';
      if (article.date_iso) time.dateTime = article.date_iso.slice(0, 10);
      time.textContent = article.date_display;
      info.appendChild(time);
    }

    const title = document.createElement('h3');
    title.className = 'blog-overview__title';
    title.textContent = article.title;
    info.appendChild(title);

    if (article.excerpt) {
      const excerpt = document.createElement('p');
      excerpt.className = 'blog-overview__excerpt tw-font-secondary';
      excerpt.textContent = article.excerpt;
      info.appendChild(excerpt);
    }

    link.appendChild(info);
    article_el.appendChild(link);
    return article_el;
  }

  _buildMedia(article, index, slot) {
    const img = article.image || {};
    if (!img.src) {
      const ph = document.createElement('div');
      ph.className = 'blog-overview__placeholder';
      return ph;
    }
    const hint = IMG_HINTS[slot.indexOf('feature') === 0 ? 'feature' : slot] || IMG_HINTS.short;
    const el = document.createElement('img');
    el.className = 'blog-overview__image';
    el.src = withWidth(img.src, hint.widths[hint.widths.length - 1]);
    el.srcset = hint.widths.map((w) => `${withWidth(img.src, w)} ${w}w`).join(', ');
    el.sizes = hint.sizes;
    el.alt = img.alt || article.title || '';
    if (img.width) el.width = img.width;
    if (img.height) el.height = img.height;
    el.loading = index < EAGER_CARDS ? 'eager' : 'lazy';
    el.fetchPriority = index < EAGER_CARDS ? 'high' : 'auto';
    return el;
  }

  _renderPagination() {
    if (!this.pagination) return;
    const total = this._filteredSorted().length;
    const pageCount = this._pageCount(total);
    this.pagination.textContent = '';
    if (pageCount <= 1) {
      this.pagination.hidden = true;
      return;
    }
    this.pagination.hidden = false;

    const ul = document.createElement('ul');
    ul.className = 'blog-overview__pagination-list';
    ul.setAttribute('role', 'list');

    const current = this.state.page;
    if (current > 1) ul.appendChild(this._pageItem(current - 1, '‹', 'prev'));
    pageWindow(current, pageCount).forEach((p) => {
      if (p === '…') {
        const li = document.createElement('li');
        const span = document.createElement('span');
        span.className = 'blog-overview__page-ellipsis';
        span.textContent = '…';
        li.appendChild(span);
        ul.appendChild(li);
      } else {
        ul.appendChild(this._pageItem(p, String(p), p === current ? 'current' : ''));
      }
    });
    if (current < pageCount) ul.appendChild(this._pageItem(current + 1, '›', 'next'));

    this.pagination.appendChild(ul);
  }

  _pageItem(page, label, variant) {
    const li = document.createElement('li');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'blog-overview__page';
    if (variant) btn.classList.add(`blog-overview__page--${variant}`);
    btn.dataset.blogPage = String(page);
    btn.textContent = label;
    // The prev/next glyphs and bare numbers read poorly on their own.
    if (variant === 'current') btn.setAttribute('aria-current', 'page');
    else if (variant === 'prev') btn.setAttribute('aria-label', this.labels.previous);
    else if (variant === 'next') btn.setAttribute('aria-label', this.labels.next);
    else btn.setAttribute('aria-label', this.labels.page.replace('__n__', label));
    li.appendChild(btn);
    return li;
  }
}

/* ---------- Helpers ---------- */

// Tag handles are rendered by Liquid's `| handle` filter (see the section's JSON
// payload) so they match the filter links exactly. Fall back to an approximation
// only for payloads without them — it can't transliterate the way Liquid does.
function tagHandles(article) {
  if (Array.isArray(article.tag_handles)) return article.tag_handles;
  return (article.tags || []).map(handleize);
}

// Rough stand-in for Shopify's `| handle` filter: accents are stripped so "Café"
// gives "cafe", but ligatures/strokes (æ, ø) still drop out — hence tag_handles.
function handleize(str) {
  return String(str)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Swap/add the `width=` param on a Shopify CDN image URL.
function withWidth(src, width) {
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return src;
  }
}

// Build a compact page-number window: 1 … c-1 c c+1 … n
function pageWindow(current, count) {
  const pages = new Set([1, count, current, current - 1, current + 1]);
  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= count)
    .sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  sorted.forEach((p) => {
    if (prev && p - prev > 1) out.push('…');
    out.push(p);
    prev = p;
  });
  return out;
}

if (!customElements.get('blog-overview')) {
  customElements.define('blog-overview', BlogOverview);
}
